import { Journeys } from "./ui/journeys";
import { AnnotationWorkspace } from "./ui/annotation-workspace";
import { Timeline, TimelineRef } from "./ui/timeline";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import {
  Box,
  Dialog,
  Flex,
  IconButton,
  VisuallyHidden,
} from "@radix-ui/themes";
import { Cross2Icon } from "@radix-ui/react-icons";
import { convertHeightToPercent, yieldToMain } from "@/shared";
import { useEffect, useRef, useState, useCallback } from "react";
import { Store3D } from "./ui/map-view/store-3d";
import { useAppDispatch, useAppSelector, useAppStore } from "@/app/appStore";
import dayjs from "dayjs";
import {
  addJourneyEvents,
  resetState,
  selectAnnotationData,
  selectAnnotationView,
  setAnnotationSaving,
  setCurrentCamera,
  setDateRange,
  setJourneyProximities,
  setJourneys,
  setJourneyTransactions,
  setOrderLoading,
  setProximityTimeRanges,
  setSelectedJourneyId,
  setSelectedOrder,
  setTrackPersistences,
} from "@/entities/annotation";
import { TimelineControls, NAVIGATION_MODES } from "./ui/timeline-controls";
import { extractAnnotationsFromOrder, OrderWithJourneys } from "./lib/helpers";
import { JourneyEvents } from "./ui/journey-events";

import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import ChromeUsageReminder from "@/shared/ui/chrome-usage-reminder";
import { isChrome } from "react-device-detect";
import AnnotationHeader from "./ui/annotation-header";
import { usePlayerActions } from "@/shared/hooks/usePlayerActions";
import { useTimelineData } from "./hooks/useTimelineData";
import { RightSidebar } from "./ui/right-sidebar";
import { toast } from "react-hot-toast";
import { NavigationItems } from "@/shared/types/navigation";
import { toSeconds } from "@/shared/helpers/datetime";
import { useParams } from "react-router-dom";
import { getDefaultCamera } from "./lib/utils";
import Header from "@/shared/components/header";
import { ZoomPanInstructions } from "./ui/zoom-pan-instructions";
import { usePlayer } from "@tezzro/player";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";

type TimerState = {
  orderId: string | null;
  isEnabled: boolean;
  timeLeft: number;
};

export function AnnotationView() {
  const { storeKey = "" } = useParams();
  const {
    addTimelineOrderEvent,
    createOrderTimelineGroup,
    addTimelineCustomerJourneyEvents,
    createJourneyTimelineGroups,
    addTimelineTransactionEvent,
    addTimelineTrackPersistenceEvent,
    addTimelineJourneyProximityEvent,
    addTimelineProximityTimeRangeEvents,
    clearTimelineData,
  } = useTimelineData();

  const { startVideo, updateVideoTimeRange } = usePlayerActions();
  const player = usePlayer();
  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );
  const annotationView = useAppSelector(selectAnnotationView);
  const annotationData = useAppSelector(selectAnnotationData);
  const isProximityTimeRangesAnnotation =
    annotationView === ANNOTATION_TYPES.PROXIMITY_TIME_RANGES &&
    !!annotationData;

  const dispatch = useAppDispatch();

  const [workspaceKey, setWorkspaceKey] = useState<string>(() =>
    crypto.randomUUID()
  );
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const store = useAppStore();

  const [timerState, setTimerState] = useState<TimerState>({
    orderId: null,
    isEnabled: false,
    timeLeft: toSeconds(30_000),
  });
  const intervalRef = useRef<NodeJS.Timeout>();
  const timeoutRef = useRef<NodeJS.Timeout>();

  const enableTimer = useCallback((orderId: string) => {
    setTimerState({
      orderId,
      isEnabled: true,
      timeLeft: toSeconds(30_000),
    });
  }, []);

  const disableTimer = useCallback((orderId: string) => {
    setTimerState((prev) =>
      prev.orderId === orderId ? { ...prev, isEnabled: false } : prev
    );
  }, []);

  useEffect(() => {
    if (!timerState.isEnabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      clearInterval(intervalRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (timerState.orderId) {
        disableTimer(timerState.orderId);
      }
    }, 30_000);

    intervalRef.current = setInterval(() => {
      setTimerState((prev) => ({
        ...prev,
        timeLeft: prev.timeLeft - 1,
      }));
    }, 1_000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerState.isEnabled, timerState.orderId, disableTimer]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useTrackedHotkeys("m", () => setIsMapDialogOpen((prev) => !prev), {
    preventDefault: true,
    enabled: !!selectedOrder && !isProximityTimeRangesAnnotation,
  });

  const populateAnnotationDetails = async (order: OrderWithJourneys) => {
    try {
      dispatch(setOrderLoading(true));

      await yieldToMain();

      const {
        journeyList,
        events,
        journeyTransactions,
        trackPersistences,
        journeyProximities,
        proximityTimeRanges,
        journeyAnalysis,
        firstJourneyId,
      } = extractAnnotationsFromOrder(order);

      addTimelineOrderEvent(order.id, new Date(order.time));
      createOrderTimelineGroup(order.id);

      if (journeyTransactions.length > 0) {
        dispatch(setJourneyTransactions(journeyTransactions));
        journeyTransactions.forEach(addTimelineTransactionEvent);
      }

      if (trackPersistences.length > 0) {
        dispatch(setTrackPersistences(trackPersistences));
        trackPersistences.forEach(addTimelineTrackPersistenceEvent);
      }

      if (journeyProximities.length > 0) {
        dispatch(setJourneyProximities(journeyProximities));
        journeyProximities.forEach(addTimelineJourneyProximityEvent);
      }

      if (proximityTimeRanges.length > 0) {
        dispatch(setProximityTimeRanges(proximityTimeRanges));
        proximityTimeRanges.forEach(addTimelineProximityTimeRangeEvents);
      }

      dispatch(setJourneys(journeyList));
      if (firstJourneyId) {
        dispatch(setSelectedJourneyId(firstJourneyId));
      }

      journeyAnalysis.forEach((analysis) => {
        createJourneyTimelineGroups(
          analysis.journeyId,
          analysis.hasTransactions,
          analysis.hasTrackPersistences,
          analysis.hasJourneyProximities,
          analysis.hasLeftHandProximityTimeRanges,
          analysis.hasRightHandProximityTimeRanges
        );
      });

      const timelineEvents = events.map((event) => ({
        id: event.id,
        type: ANNOTATION_TYPES.JOURNEY,
        journeyId: event.journeyId,
        timestamp: event?.timestamp?.toISOString(),
      }));

      addTimelineCustomerJourneyEvents(timelineEvents);

      dispatch(addJourneyEvents(events));
    } catch (error) {
      toast.error("Failed to populate annotation details");
      console.log("error ", error);
    } finally {
      dispatch(setAnnotationSaving(false));
      dispatch(setOrderLoading(false));
    }
  };

  const onFinishAnnotation = () => {
    if (selectedOrder) {
      clearTimelineData();
      player.mediaPlayer.pause();
    }
  };

  const onSelectOrder = (
    order: OrderWithJourneys,
    shouldUpdateCurrentTime = false
  ) => {
    const defaultCamera = getDefaultCamera(storeKey);
    const {
      annotation: { timelineRange },
    } = store.getState();
    const start = dayjs(order.time)
      .subtract(Number(timelineRange.beforeMinutes), "minute")
      .toDate();
    const end = dayjs(order.time)
      .add(Number(timelineRange.afterMinutes), "minute")
      .toDate();
    const orderTimestamp = new Date(order.time).getTime();

    const isLive =
      dayjs().diff(dayjs(order.time), "minute") <=
      Number(timelineRange.afterMinutes);

    dispatch(setSelectedOrder(order));
    dispatch(setDateRange({ start, end }));

    timelineRef.current?.setTimelineRange(start, end, {
      clearDataset: true,
      forceZoom: true,
    });

    if (shouldUpdateCurrentTime) {
      dispatch(setCurrentCamera(defaultCamera));
      startVideo({
        seekValueUTC: orderTimestamp,
        cameraId: defaultCamera,
        isLive,
      });
    }

    populateAnnotationDetails(order);
  };

  const timelineRef = useRef<TimelineRef | null>(null);

  const handleUpdateZoom = (zoomLevel: number) => {
    timelineRef.current?.adjustZoomLevel(zoomLevel);
  };

  const handleRangeChange = (params: {
    start: Date;
    end: Date;
    beforeMinutes: number;
    afterMinutes: number;
  }) => {
    const { start, end, afterMinutes } = params;
    const {
      annotation: { selectedOrder },
    } = store.getState();

    const isLive =
      dayjs().diff(dayjs(selectedOrder?.time), "minute") <= afterMinutes;

    timelineRef.current?.setTimelineRange(start, end, { clearDataset: false });
    dispatch(setDateRange({ start, end }));
    updateVideoTimeRange({
      start,
      end,
      isLive,
    });
  };

  const handleTimerEnable = useCallback(
    (orderId: string) => {
      enableTimer(orderId);
    },
    [enableTimer]
  );

  const handleTimerDisable = useCallback(
    (orderId: string) => {
      disableTimer(orderId);
    },
    [disableTimer]
  );

  const isTimerEnabled =
    timerState.isEnabled && timerState.orderId === selectedOrder?.id;

  useEffect(() => {
    window.dashPlayer = player.mediaPlayer;
  }, [player.mediaPlayer]);

  return (
    <>
      {!isChrome && <ChromeUsageReminder />}
      <div className="max-w-screen w-screen h-screen">
        <PanelGroup direction="vertical">
          <Header
            title={NavigationItems.LOD.name}
            exclude={[NavigationItems.LOD.id]}
          />
          <Panel id="some-2" collapsible>
            <Flex width="100%" justify="between" height="100%">
              <Box
                className="border-r"
                width="300px"
                minWidth="300px"
                height="100%"
              >
                <PanelGroup direction="vertical">
                  <Journeys />
                  <JourneyEvents />
                </PanelGroup>
              </Box>

              <Flex flexGrow="1" minWidth="0">
                <Flex direction="column" flexGrow="1" minWidth="0">
                  <AnnotationHeader
                    onOpenMap={() => setIsMapDialogOpen(true)}
                  />
                  <Flex
                    justify="center"
                    className="relative h-full max-w-full max-h-full flex justify-center items-center bg-black text-white"
                  >
                    <AnnotationWorkspace
                      key={workspaceKey}
                      selectedOrder={selectedOrder}
                    />
                  </Flex>
                  <ZoomPanInstructions />
                </Flex>
              </Flex>

              <Box
                className="border-l"
                width="350px"
                minWidth="350px"
                height="100%"
              >
                <RightSidebar
                  setWorkspaceKey={setWorkspaceKey}
                  onSelectOrder={onSelectOrder}
                  onFinishAnnotation={onFinishAnnotation}
                  timerEnabled={isTimerEnabled}
                  timerTimeLeft={timerState.timeLeft}
                  onTimerEnable={handleTimerEnable}
                  onTimerDisable={handleTimerDisable}
                />
              </Box>
            </Flex>
          </Panel>

          <PanelResizeHandle className="h-2 bg-gray-200 active:bg-gray-600 hover:bg-gray-400 hover:transition-1" />
          <Panel
            id="playback-controls"
            defaultSize={convertHeightToPercent(250)}
            collapsible
          >
            <div className="w-full bg-white h-full border-top shadow-sidebar">
              <TimelineControls
                onUpdateZoom={handleUpdateZoom}
                onRangeChange={handleRangeChange}
                navigationMode={NAVIGATION_MODES.ANNOTATION}
              />
              <Timeline ref={timelineRef} />
            </div>
          </Panel>
        </PanelGroup>
      </div>
      <Dialog.Root open={isMapDialogOpen} onOpenChange={setIsMapDialogOpen}>
        <Dialog.Content
          minWidth="90vw"
          height="90vh"
          className="p-0 backdrop-blur-sm"
        >
          <VisuallyHidden>
            <Dialog.Title>Map view</Dialog.Title>
          </VisuallyHidden>
          <Box className="absolute top-2 right-2 z-10">
            <Dialog.Close>
              <IconButton variant="solid" color="gray" highContrast>
                <Cross2Icon />
              </IconButton>
            </Dialog.Close>
          </Box>
          <Store3D onSelectCamera={() => setIsMapDialogOpen(false)} />
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
